import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  AgricolaBeneficiaryDialogData,
  InsuranceBeneficiary
} from '../../../../../interfaces/agricola.interface';
import { SisSelectOption } from '../../../../sis/sis-select/sis-select.component';

@Component({
  selector: 'app-agricola-beneficiary-dialog',
  templateUrl: './agricola-beneficiary-dialog.component.html',
  styleUrls: ['./agricola-beneficiary-dialog.component.css'],
  standalone: false
})
export class AgricolaBeneficiaryDialogComponent implements OnInit, OnDestroy {

  form!: FormGroup;
  showErrorBanner = false;
  relationshipOptions: SisSelectOption[] = [];
  relationshipLoading = false;
  relationshipLoadedKey = 0;
  relationshipLoadFailed = false;
  private readonly subscriptions = new Subscription();
  readonly identificationTypeOptions: SisSelectOption[] = [
    { label: 'Número único', value: '1' },
    { label: 'DUI', value: '2' },
    { label: 'NIT', value: '3' },
    { label: 'Carnet residente', value: '4' },
    { label: 'Pasaporte', value: '5' }
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AgricolaBeneficiaryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AgricolaBeneficiaryDialogData
  ) {}

  ngOnInit(): void {
    this.relationshipOptions = this.data.relationshipOptions;
    this.bindRelationshipCatalogState();
    const b = this.data.beneficiary;
    this.form = this.fb.group({
      name: [b?.name || '', Validators.required],
      relationship: [b?.relationship || '', Validators.required],
      percentage: [
        b?.percentage || null,
        [
          Validators.required,
          Validators.min(1),
          Validators.max(this.data.availableParticipation)
        ]
      ],
      identification_id: [
        b?.identification_id || '',
        [
          Validators.required,
          this.identificationNotDuplicatedValidator(),
          this.identificationNotInsuredValidator()
        ]
      ],
      identification_type: [b?.identification_type == null ? '' : String(b.identification_type), Validators.required],
      birthday: [b?.birthday || '']
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get availableLabel(): string {
    return `Participación disponible ${this.data.availableParticipation}%`;
  }

  get identificationError(): string {
    const control = this.form?.get('identification_id');
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('duplicateIdentification')) {
      return 'Este número de identificación ya fue agregado.';
    }

    if (control.hasError('insuredIdentification')) {
      return 'El beneficiario no puede ser el asegurado.';
    }

    return 'Ingresa número de identificación.';
  }

  get percentageError(): string {
    const control = this.form?.get('percentage');
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    return `Ingresa porcentaje válido (1 - ${this.data.availableParticipation}).`;
  }

  get relationshipCaption(): string {
    return this.relationshipOptions.length ? `${this.relationshipOptions.length} opciones` : '';
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.showErrorBanner = true;
      return;
    }
    this.showErrorBanner = false;
    const value = this.form.value;
    // Relationship is kept as the raw catalog code selected in the UI.
    // Provider conversion remains backend-driven: default S2_PLUS->CHUBB,
    // external_bcr ASSA->CHUBB, or DESCRIPTION pass-through by product config.
    const beneficiary: InsuranceBeneficiary = {
      name: String(value.name || '').trim(),
      relationship: String(value.relationship || ''),
      percentage: Number(value.percentage),
      identification_id: String(value.identification_id || '').trim(),
      identification_type: value.identification_type === '' ? null : Number(value.identification_type)
    };

    const birthday = this.toIsoDate(value.birthday);
    if (birthday) {
      beneficiary.birthday = birthday;
    }

    this.dialogRef.close(beneficiary);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  retryRelationship(): void {
    this.data.retryRelationship?.();
  }

  private bindRelationshipCatalogState(): void {
    if (this.data.relationshipOptions$) {
      this.subscriptions.add(this.data.relationshipOptions$.subscribe(options => {
        this.relationshipOptions = options;
      }));
    }

    if (this.data.relationshipLoading$) {
      this.subscriptions.add(this.data.relationshipLoading$.subscribe(loading => {
        this.relationshipLoading = loading;
      }));
    }

    if (this.data.relationshipLoadedKey$) {
      this.subscriptions.add(this.data.relationshipLoadedKey$.subscribe(loadedKey => {
        this.relationshipLoadedKey = loadedKey;
      }));
    }

    if (this.data.relationshipLoadFailed$) {
      this.subscriptions.add(this.data.relationshipLoadFailed$.subscribe(loadFailed => {
        this.relationshipLoadFailed = loadFailed;
      }));
    }
  }

  private identificationNotDuplicatedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = this.normalizeId(control.value);
      if (!value) {
        return null;
      }

      const existing = (this.data.existingIdentificationIds || []).map(id => this.normalizeId(id));
      return existing.includes(value) ? { duplicateIdentification: true } : null;
    };
  }

  private identificationNotInsuredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = this.normalizeId(control.value);
      const insured = this.normalizeId(this.data.insuredIdentificationId);
      return value && insured && value === insured ? { insuredIdentification: true } : null;
    };
  }

  private normalizeId(value: unknown): string {
    return String(value || '').trim().toUpperCase();
  }

  private toIsoDate(value: unknown): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }

    const isoParts = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoParts) {
      return [
        isoParts[1],
        isoParts[2].padStart(2, '0'),
        isoParts[3].padStart(2, '0')
      ].join('-');
    }

    const slashParts = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashParts) {
      return [
        slashParts[3],
        slashParts[2].padStart(2, '0'),
        slashParts[1].padStart(2, '0')
      ].join('-');
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${parsed.getFullYear()}-${month}-${day}`;
  }
}
